import type { RigKind } from "./Carry";
import { CHAPTERS, ITEM_LABELS, LORE } from "./data";
import type {
  ChapterId,
  DialogPage,
  GameSettings,
  SaveData,
} from "./types";

/** Toutes les variantes de premier plan, pour pouvoir toutes les éteindre. */
const RIG_KINDS: readonly RigKind[] = [
  "none",
  "tower",
  "screen",
  "crate",
  "box",
  "dolly",
  "wheel",
];

interface MenuCallbacks {
  onNew: () => void;
  onContinue: () => void;
  onSettings: () => void;
}

type ToggleSettingKey =
  | "music"
  | "sound"
  | "reducedMotion"
  | "highContrast"
  | "pointerLock";

export class UI {
  readonly canvasHost: HTMLElement;
  readonly panel: HTMLElement;

  private readonly menu: HTMLElement;
  private readonly hud: HTMLElement;
  private readonly dialogLayer: HTMLElement;
  private readonly chapterCard: HTMLElement;
  private readonly prompt: HTMLElement;
  private readonly toastHost: HTMLElement;
  private readonly pauseLayer: HTMLElement;
  private readonly inventoryLayer: HTMLElement;
  private readonly controlHint: HTMLElement;
  private readonly firstPersonRig: HTMLElement;
  private modalOpen = false;

  constructor(private readonly mount: HTMLElement) {
    mount.innerHTML = `
      <div class="game-shell">
        <div class="canvas-host" data-canvas-host></div>
        <div class="scanlines" aria-hidden="true"></div>
        <main class="menu-layer" data-menu>
          <div class="menu-noise" aria-hidden="true"></div>
          <section class="title-card">
            <div class="eyebrow">UNE AVENTURE BEAUCOUP TROP LOCALE</div>
            <div class="title-lockup" aria-label="Snitch LAN — La Chaise 404">
              <span class="title-snitch">SNITCH</span>
              <span class="title-lan">LAN</span>
              <span class="title-sub">LA CHAISE <b>404</b></span>
            </div>
            <p class="menu-tagline">
              Un PC de 18 kg. Une nuit blanche.<br />
              Zéro place de stationnement.
            </p>
            <div class="menu-actions">
              <button class="button primary" data-new>
                <span>Nouvelle partie</span><kbd>↵</kbd>
              </button>
              <button class="button" data-continue>
                <span>Continuer</span><small data-save-detail></small>
              </button>
              <button class="button ghost" data-settings>Paramètres</button>
            </div>
            <div class="menu-meta">
              <span>15–30 min</span><i></i><span>Sauvegarde auto</span><i></i><span>FR-QC</span>
            </div>
          </section>
          <aside class="prophecy-card">
            <span class="tape">CONFIDENTIEL</span>
            <p>« Quand le dernier câble Cat-5e sera déroulé dans le mauvais sens, Gérard-DHCP se réveillera. »</p>
            <small>— Prophétie trouvée sous une table pliante, 2007</small>
          </aside>
          <div class="version">BUILD 1.0 // PING: probablement</div>
        </main>

        <section class="hud" data-hud aria-live="polite">
          <div class="hud-top">
            <div class="chapter-badge">
              <small data-chapter-number>ACTE I</small>
              <strong data-chapter-title>Le purgatoire asphalté</strong>
            </div>
            <div class="meters">
              <div class="meter energy">
                <span>ÉNERGIE</span>
                <div><i data-energy-bar></i></div>
                <b data-energy-value>100</b>
              </div>
              <div class="meter suspicion">
                <span>SOUPÇON</span>
                <div><i data-suspicion-bar></i></div>
                <b data-suspicion-value>4</b>
              </div>
              <div class="meter reputation">
                <span>RÉPUTATION</span>
                <div><i data-reputation-bar></i></div>
                <b data-reputation-value>0</b>
              </div>
            </div>
            <div class="carry-chip" data-carry-chip hidden>
              <small data-carry-title>MAINS</small>
              <div class="carry-dots" data-carry-dots><i></i><i></i><i></i></div>
              <strong data-carry-label>Vides</strong>
            </div>
            <div class="hud-buttons">
              <button class="icon-button" data-inventory aria-label="Inventaire">🎒</button>
              <button class="icon-button" data-pause aria-label="Pause">Ⅱ</button>
            </div>
          </div>
          <div class="objective-card">
            <span class="objective-pulse"></span>
            <div><small>OBJECTIF ACTUEL</small><strong data-objective>Retrouver toute dignité.</strong></div>
            <span data-objective-progress></span>
          </div>
          <div class="controls-hint"><kbd>WASD</kbd> Bouger <kbd>SOURIS</kbd> Regarder <kbd>⇧</kbd> Courir <kbd>E</kbd> Interagir <kbd>F</kbd> Monter/descendre</div>
        </section>

        <div class="first-person-rig" data-fp-rig aria-hidden="true">
          <div class="fp-tower">
            <i class="fp-handle"></i>
            <span class="fp-drive"></span>
            <span class="fp-floppy"></span>
            <b class="fp-vents"></b>
            <em class="fp-power"></em>
            <small>GISÈLE • P4</small>
            <u class="fp-grip"></u>
            <u class="fp-grip fp-grip-right"></u>
          </div>
          <div class="fp-crt">
            <i class="fp-crt-glass"></i>
            <b class="fp-crt-vents"></b>
            <small>VGA 19 PO</small>
          </div>
          <div class="fp-bin">
            <i class="fp-bin-keys"></i>
            <b class="fp-bin-mouse"></b>
            <small>PÉRIPHÉRIQUES</small>
          </div>
          <div class="fp-box"><i class="fp-box-lid"></i><small data-rig-label>CHARGE</small></div>
          <div class="fp-dolly"><i></i><b></b><span></span></div>
          <div class="fp-wheel"><i></i></div>
        </div>
        <div class="crosshair" data-crosshair aria-hidden="true"><i></i><b></b></div>

        <div class="interaction-prompt" data-prompt>
          <kbd>E</kbd><span data-prompt-label>Interagir</span>
        </div>

        <div class="control-hint" data-control-hint aria-hidden="true">
          <span class="mouse-icon" aria-hidden="true"></span>
          <span>Clique n’importe où pour reprendre la vue</span>
        </div>

        <section class="dialog-layer" data-dialog role="dialog" aria-modal="true">
          <div class="dialog-card">
            <div class="portrait" data-dialog-portrait>🧔</div>
            <div class="dialog-content">
              <div class="dialog-speaker" data-dialog-speaker>Patoche</div>
              <p data-dialog-text></p>
              <div class="dialog-choices" data-dialog-choices></div>
              <button class="dialog-next" data-dialog-next>Continuer <kbd>E</kbd></button>
            </div>
          </div>
        </section>

        <section class="panel-layer" data-panel role="dialog" aria-modal="true"></section>

        <section class="chapter-card-layer" data-chapter-card>
          <div class="chapter-line"></div>
          <small data-card-number>ACTE I</small>
          <h2 data-card-title>Le purgatoire asphalté</h2>
          <p data-card-subtitle></p>
          <div class="chapter-line"></div>
        </section>

        <section class="pause-layer" data-pause-layer>
          <div class="pause-card">
            <small>LE TEMPS EST SUSPENDU</small>
            <h2>Pause tactique</h2>
            <p>Gisèle continue probablement à chauffer.</p>
            <button class="button primary" data-resume>Reprendre</button>
            <button class="button" data-pause-settings>Paramètres</button>
            <button class="button ghost" data-menu-return>Menu principal</button>
          </div>
        </section>

        <section class="inventory-layer" data-inventory-layer>
          <div class="inventory-drawer">
            <header>
              <div><small>SAC À DOS DIMENSIONNEL</small><h2>Inventaire & archives</h2></div>
              <button class="icon-button" data-inventory-close>×</button>
            </header>
            <div class="inventory-columns">
              <div>
                <h3>Objets importants</h3>
                <div class="item-list" data-item-list></div>
              </div>
              <div>
                <h3>Archives du LAN</h3>
                <div class="lore-list" data-lore-list></div>
              </div>
            </div>
          </div>
        </section>

        <div class="toast-host" data-toasts aria-live="assertive"></div>

        <div class="touch-controls">
          <div class="joystick" data-joystick><i data-joystick-knob></i></div>
          <div class="touch-actions">
            <button data-touch-sprint>⇧<small>COURIR</small></button>
            <button class="touch-vehicle" data-touch-vehicle hidden>F<small>SORTIR</small></button>
            <button class="touch-action" data-touch-action>E<small>ACTION</small></button>
          </div>
        </div>
      </div>
    `;

    this.canvasHost = this.required("[data-canvas-host]");
    this.menu = this.required("[data-menu]");
    this.hud = this.required("[data-hud]");
    this.dialogLayer = this.required("[data-dialog]");
    this.panel = this.required("[data-panel]");
    this.chapterCard = this.required("[data-chapter-card]");
    this.prompt = this.required("[data-prompt]");
    this.toastHost = this.required("[data-toasts]");
    this.pauseLayer = this.required("[data-pause-layer]");
    this.inventoryLayer = this.required("[data-inventory-layer]");
    this.controlHint = this.required("[data-control-hint]");
    this.firstPersonRig = this.required("[data-fp-rig]");

  }

  bindMenu(hasSave: boolean, save: SaveData | null, callbacks: MenuCallbacks): void {
    const continueButton = this.required<HTMLButtonElement>("[data-continue]");
    continueButton.disabled = !hasSave;
    const detail = this.required("[data-save-detail]");
    detail.textContent =
      hasSave && save
        ? `${CHAPTERS[save.chapter].number} · ${this.formatTime(save.elapsedSeconds)}`
        : "Aucune sauvegarde";
    this.required("[data-new]").onclick = callbacks.onNew;
    continueButton.onclick = callbacks.onContinue;
    this.required("[data-settings]").onclick = callbacks.onSettings;
  }

  bindPause(callbacks: {
    onPause: () => void;
    onResume: () => void;
    onSettings: () => void;
    onMenu: () => void;
  }): void {
    this.required("[data-pause]").onclick = callbacks.onPause;
    this.required("[data-resume]").onclick = callbacks.onResume;
    this.required("[data-pause-settings]").onclick = callbacks.onSettings;
    this.required("[data-menu-return]").onclick = callbacks.onMenu;
  }

  bindInventory(callbacks: { onOpen: () => void; onClose: () => void }): void {
    this.required("[data-inventory]").onclick = () => {
      this.inventoryLayer.classList.add("visible");
      this.modalOpen = true;
      callbacks.onOpen();
    };
    this.required("[data-inventory-close]").onclick = () => {
      this.inventoryLayer.classList.remove("visible");
      this.modalOpen = false;
      callbacks.onClose();
    };
  }

  showInventory(show: boolean): void {
    this.inventoryLayer.classList.toggle("visible", show);
    this.modalOpen = show;
  }

  showMenu(show: boolean): void {
    this.menu.classList.toggle("hidden", !show);
    if (show) {
      this.hud.classList.remove("visible");
      this.setPrompt(null);
      this.setControlHint(false);
      this.setRig("none");
      this.setVehicleControl(false, false);
    }
  }

  setHUDVisible(show: boolean): void {
    this.hud.classList.toggle("visible", show);
    this.required("[data-crosshair]").classList.toggle("visible", show);
  }

  /**
   * Le premier plan montre ce que les mains tiennent réellement. C'est la seule
   * confirmation que le joueur a du poids qu'il transporte, alors elle doit
   * suivre chaque changement de charge, pas seulement les changements de
   * chapitre.
   */
  setRig(kind: RigKind, label = ""): void {
    const rig = this.firstPersonRig;
    rig.classList.toggle("visible", kind !== "none");
    for (const candidate of RIG_KINDS) {
      rig.classList.toggle(`carry-${candidate}`, candidate === kind);
    }
    // La caisse générique sert à plusieurs objets : elle porte leur nom.
    const caption = rig.querySelector("[data-rig-label]");
    if (caption) caption.textContent = label.toUpperCase();
  }

  /** Pastille « MAINS » du HUD : combien de place il reste, et pour quoi. */
  setCarry(bulk: number, capacity: number, label: string, driving: boolean): void {
    const chip = this.required("[data-carry-chip]");
    chip.hidden = driving;
    if (driving) return;
    this.required("[data-carry-label]").textContent = label;
    const dots = [...chip.querySelectorAll<HTMLElement>("[data-carry-dots] i")];
    dots.forEach((dot, index) => {
      dot.classList.toggle("filled", index < bulk);
      dot.hidden = index >= capacity;
    });
    chip.classList.toggle("full", bulk >= capacity);
  }

  /** Le bouton tactile « sortir / embarquer » n'existe qu'au stationnement. */
  setVehicleControl(show: boolean, driving: boolean): void {
    const button = this.mount.querySelector<HTMLButtonElement>("[data-touch-vehicle]");
    if (!button) return;
    button.hidden = !show;
    const caption = button.querySelector("small");
    if (caption) caption.textContent = driving ? "SORTIR" : "MONTER";
  }

  setMoving(moving: boolean, running = false): void {
    this.firstPersonRig.classList.toggle("moving", moving);
    this.firstPersonRig.classList.toggle("running", running);
  }

  /**
   * A slim, non-blocking pill. The game re-acquires the pointer lock on its own
   * whenever a menu closes, so this only appears when the browser refuses.
   */
  setControlHint(show: boolean): void {
    this.controlHint.classList.toggle("visible", show);
  }

  setChapter(chapter: ChapterId): void {
    const info = CHAPTERS[chapter];
    this.required("[data-chapter-number]").textContent = info.number;
    this.required("[data-chapter-title]").textContent = info.title;
  }

  updateHUD(save: SaveData): void {
    const energy = Math.max(0, Math.min(100, save.stats.energy));
    const suspicion = Math.max(0, Math.min(100, save.stats.suspicion));
    const reputation = Math.max(0, Math.min(100, save.stats.reputation));
    this.required("[data-energy-bar]").style.width = `${energy}%`;
    this.required("[data-suspicion-bar]").style.width = `${suspicion}%`;
    this.required("[data-reputation-bar]").style.width = `${reputation}%`;
    this.required("[data-energy-value]").textContent = String(Math.round(energy));
    this.required("[data-suspicion-value]").textContent = String(Math.round(suspicion));
    this.required("[data-reputation-value]").textContent = String(Math.round(reputation));
  }

  setObjective(text: string, progress = ""): void {
    this.required("[data-objective]").textContent = text;
    this.required("[data-objective-progress]").textContent = progress;
  }

  setPrompt(label: string | null): void {
    if (!label) {
      this.prompt.classList.remove("visible");
      return;
    }
    this.required("[data-prompt-label]").textContent = label;
    this.prompt.classList.add("visible");
  }

  async showChapterCard(chapter: ChapterId): Promise<void> {
    const info = CHAPTERS[chapter];
    this.required("[data-card-number]").textContent = info.number;
    this.required("[data-card-title]").textContent = info.title;
    this.required("[data-card-subtitle]").textContent = info.subtitle;
    this.chapterCard.classList.add("visible");
    await this.wait(2250);
    this.chapterCard.classList.remove("visible");
    await this.wait(420);
  }

  async dialog(pages: DialogPage[]): Promise<number[]> {
    this.modalOpen = true;
    this.dialogLayer.classList.add("visible");
    const results: number[] = [];

    for (const page of pages) {
      const speaker = this.required("[data-dialog-speaker]");
      const text = this.required("[data-dialog-text]");
      const portrait = this.required("[data-dialog-portrait]");
      const choices = this.required("[data-dialog-choices]");
      const next = this.required<HTMLButtonElement>("[data-dialog-next]");
      speaker.textContent = page.speaker;
      text.textContent = page.text;
      portrait.textContent = page.portrait ?? "🧔";
      choices.innerHTML = "";
      next.hidden = Boolean(page.choices?.length);

      const selected = await new Promise<number>((resolve) => {
        if (page.choices?.length) {
          page.choices.forEach((choice, index) => {
            const button = document.createElement("button");
            button.className = "choice-button";
            button.disabled = choice.disabled ?? false;
            button.innerHTML = `<span>${choice.label}</span>${
              choice.hint ? `<small>${choice.hint}</small>` : ""
            }`;
            button.onclick = () => {
              choice.action();
              resolve(index);
            };
            choices.append(button);
          });
        } else {
          // The pointer stays locked through choice-less dialogue, so the click
          // never reaches the button — listen on the window as well.
          const go = (): void => {
            window.removeEventListener("keydown", onKey);
            window.removeEventListener("mousedown", onPointer);
            resolve(0);
          };
          const onKey = (event: KeyboardEvent): void => {
            if (event.code === "KeyE" || event.code === "Enter" || event.code === "Space") {
              event.preventDefault();
              go();
            }
          };
          const onPointer = (event: MouseEvent): void => {
            if (event.button === 0) go();
          };
          next.onclick = go;
          window.addEventListener("keydown", onKey);
          window.addEventListener("mousedown", onPointer);
        }
      });
      results.push(selected);
    }

    this.dialogLayer.classList.remove("visible");
    this.modalOpen = false;
    return results;
  }

  openPanel(title: string, subtitle = ""): HTMLElement {
    this.modalOpen = true;
    this.panel.innerHTML = `
      <div class="game-panel">
        <header class="panel-header">
          <div><small>MINI-JEU HOMOLOGUÉ</small><h2>${title}</h2><p>${subtitle}</p></div>
        </header>
        <div class="panel-body" data-panel-body></div>
      </div>
    `;
    this.panel.classList.add("visible");
    return this.required("[data-panel-body]");
  }

  closePanel(): void {
    this.panel.classList.remove("visible");
    this.panel.innerHTML = "";
    this.modalOpen = false;
  }

  showPause(show: boolean): void {
    this.pauseLayer.classList.toggle("visible", show);
    this.modalOpen = show;
  }

  showSettings(
    settings: GameSettings,
    onChange: (settings: GameSettings) => void,
    onClose?: () => void,
  ): void {
    const body = this.openPanel("Paramètres", "Ajuster l’expérience sans redémarrer Gérard.");
    body.innerHTML = `
      <div class="settings-grid">
        ${this.settingToggle("music", "Musique procédurale", "Bips inspirés par 2003.", settings.music)}
        ${this.settingToggle("sound", "Effets sonores", "Clics, erreurs et café.", settings.sound)}
        ${this.settingToggle("reducedMotion", "Réduire les mouvements", "Moins de caméra et de transitions.", settings.reducedMotion)}
        ${this.settingToggle("highContrast", "Contraste élevé", "HUD plus lisible.", settings.highContrast)}
        ${this.settingToggle("pointerLock", "Capturer la souris", "Désactive-le pour viser en glissant la souris.", settings.pointerLock)}
        ${this.settingRange("Sensibilité de la caméra", "Plus bas = mouvements plus calmes.", settings.lookSensitivity)}
      </div>
      <button class="button primary panel-done" data-settings-done>Terminé</button>
    `;
    body.querySelectorAll<HTMLInputElement>("input[data-setting]").forEach((input) => {
      input.onchange = () => {
        settings[input.dataset.setting as ToggleSettingKey] = input.checked;
        document.documentElement.classList.toggle("high-contrast", settings.highContrast);
        document.documentElement.classList.toggle("reduced-motion", settings.reducedMotion);
        onChange({ ...settings });
      };
    });
    const sensitivity = body.querySelector<HTMLInputElement>(
      "[data-look-sensitivity]",
    );
    const sensitivityValue = body.querySelector<HTMLOutputElement>(
      "[data-look-sensitivity-value]",
    );
    if (sensitivity && sensitivityValue) {
      sensitivity.oninput = () => {
        settings.lookSensitivity = Number(sensitivity.value);
        sensitivityValue.value = `${Math.round(settings.lookSensitivity * 100)} %`;
        sensitivity.setAttribute(
          "aria-valuetext",
          `${Math.round(settings.lookSensitivity * 100)} %`,
        );
        onChange({ ...settings });
      };
    }
    body.querySelector<HTMLButtonElement>("[data-settings-done]")!.onclick = () => {
      this.closePanel();
      onClose?.();
    };
  }

  refreshInventory(save: SaveData): void {
    const items = this.required("[data-item-list]");
    items.innerHTML = save.inventory.length
      ? save.inventory
          .map(
            (id) =>
              `<div class="inventory-item"><span>${this.itemIcon(id)}</span><div><strong>${
                ITEM_LABELS[id] ?? id
              }</strong><small>Objet narrativement indispensable</small></div></div>`,
          )
          .join("")
      : `<div class="empty-state">Ton sac contient seulement de l’espoir.</div>`;

    const lore = this.required("[data-lore-list]");
    const entries = Object.entries(LORE);
    lore.innerHTML = entries
      .map(([id, archive], index) => {
        const unlocked = save.lore.includes(id);
        return `<article class="lore-entry ${unlocked ? "" : "locked"}">
          <span>${String(index + 1).padStart(2, "0")}</span>
          <div><strong>${unlocked ? archive.title : "Archive chiffrée"}</strong>
          <p>${unlocked ? archive.body : "Trouve le terminal correspondant dans le monde."}</p></div>
        </article>`;
      })
      .join("");
  }

  toast(message: string, tone: "good" | "bad" | "neutral" = "neutral"): void {
    const toast = document.createElement("div");
    toast.className = `toast ${tone}`;
    toast.innerHTML = `<i></i><span>${message}</span>`;
    this.toastHost.append(toast);
    window.setTimeout(() => toast.classList.add("leaving"), 3100);
    window.setTimeout(() => toast.remove(), 3500);
  }

  isModalOpen(): boolean {
    return this.modalOpen;
  }

  private settingToggle(
    key: ToggleSettingKey,
    label: string,
    help: string,
    checked: boolean,
  ): string {
    return `<label class="setting-row"><div><strong>${label}</strong><small>${help}</small></div>
      <input type="checkbox" data-setting="${key}" ${checked ? "checked" : ""}/><i></i></label>`;
  }

  private settingRange(label: string, help: string, value: number): string {
    return `<div class="setting-row setting-range"><label for="look-sensitivity"><strong>${label}</strong><small>${help}</small></label>
      <span><output for="look-sensitivity" data-look-sensitivity-value>${Math.round(value * 100)} %</output>
      <input id="look-sensitivity" type="range" min="0.35" max="1.25" step="0.05" value="${value}" data-look-sensitivity aria-valuetext="${Math.round(value * 100)} %"/></span></div>`;
  }

  private itemIcon(id: string): string {
    if (id.includes("cable")) return "🔌";
    if (id.includes("ticket") || id.includes("courriel") || id.includes("preuve")) return "🧾";
    if (id === "badge") return "🪪";
    if (id.includes("cafe")) return "☕";
    if (id.includes("rhum") || id === "flasque" || id.includes("mana")) return "🧃";
    if (id.includes("usb")) return "💾";
    if (id.includes("tour")) return "🖥️";
    return "📦";
  }

  private formatTime(seconds: number): string {
    const minutes = Math.floor(seconds / 60);
    const rest = Math.floor(seconds % 60);
    return `${minutes}:${String(rest).padStart(2, "0")}`;
  }

  private required<T extends HTMLElement = HTMLElement>(selector: string): T {
    const element = this.mount.querySelector<T>(selector);
    if (!element) throw new Error(`Élément UI introuvable: ${selector}`);
    return element;
  }

  private wait(ms: number): Promise<void> {
    return new Promise((resolve) => window.setTimeout(resolve, ms));
  }
}
